import {
  customElement,
  DeesElement,
  property,
  html,
  TemplateResult,
  css,
  cssManager,
  state,
} from '@designestate/dees-element';
import * as domtools from '@designestate/dees-domtools';

// third party catalogs
import '@uptimelink/webwidget';
import { DeesForm, DeesFormSubmit, DeesInputText } from '@designestate/dees-catalog';

// interfaces
import * as lointReception from '@losslessone_private/loint-reception';

declare global {
  interface HTMLElementTagNameMap {
    'lele-login': LeleLogin;
  }
}

@customElement('lele-login')
export class LeleLogin extends DeesElement {
  public static demo = () => html`<lele-login></lele-login>`;
  public static receptionUrl = 'https://reception.lossless.one/typedrequest';

  @property()
  public activePane: 'login' | 'register' = 'login';

  @property()
  public productOfInterest: string;

  @property()
  jwt: string;

  public jwtObserable = new domtools.rxjs.Subject<string>();

  constructor() {
    super();
    domtools.elementBasic.setup();
  }

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        max-width: 520px;
        margin-left: auto;
        margin-right: auto;
        color: ${cssManager.bdTheme('#333333', '#ffffff')};
      }

      .box {
        cursor: pointer;
        overflow: hidden;
        transition: all 0.2s ease;
        box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
        border-radius: 3px;
        margin-bottom: 20px;
        height: 56px;
        background: ${cssManager.bdTheme('#ffffff', '#181818')};
        border-top: 1px solid ${cssManager.bdTheme('#ffffff', '#333333')};
      }

      .box.active {
        height: 400px;
        cursor: auto;
        border-top: 1px solid #e4002b;
      }

      .loginbox {
      }

      .registerbox {
      }

      .boxheading {
        text-align: center;
        line-height: 56px;
        font-size: 20px;
      }

      .boxcontent {
        margin: 0px 20px;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="loginbox box ${this.activePane === 'login' ? 'active' : ''}">
        <div
          class="boxheading"
          @click="${() => {
            this.activePane = 'login';
          }}"
        >
          Login
        </div>
        <uptimelink-webwidget></uptimelink-webwidget>
        <div class="boxcontent">
          <dees-form
            id="loginForm"
            @formData="${(eventArg) => {
              this.login({
                emailAddress: eventArg.detail.data.emailAddress,
                passwordArg: eventArg.detail.data.password,
              });
            }}"
          >
            <dees-input-text
              id="loginEmailInput"
              .required="${true}"
              key="emailAddress"
              label="Email-Address or Username"
            ></dees-input-text>
            <dees-input-text
              id="loginPasswordInput"
              key="password"
              label="Password"
            ></dees-input-text>
            <dees-form-submit id="loginSubmitButton"></dees-form-submit>
          </dees-form>
        </div>
      </div>
      <div class="registerbox box ${this.activePane === 'register' ? 'active' : ''}">
        <div
          class="boxheading "
          @click="${() => {
            this.activePane = 'register';
          }}"
        >
          Register
        </div>
        <uptimelink-webwidget></uptimelink-webwidget>
        <div class="boxcontent">
          <dees-form
            id="registrationForm"
            @formData="${(eventArg) => {
              this.register({
                emailAddress: eventArg.detail.data.emailAddress,
              });
            }}"
          >
            <dees-input-text
              .required=${true}
              key="emailAddress"
              label="Email-Address"
            ></dees-input-text>
            <dees-form-submit>Send Verification Email</dees-form-submit>
          </dees-form>
        </div>
      </div>
    `;
  }

  public async firstUpdated() {
    await this.domtoolsPromise;
    const loginForm: DeesForm = this.shadowRoot.querySelector('#loginForm');
    const loginPasswordInput: DeesInputText = loginForm.querySelector('#loginPasswordInput');
    const loginSubmitButton: DeesFormSubmit = loginForm.querySelector('#loginSubmitButton');
    const setButtonText = async () => {
      if (loginPasswordInput.value) {
        loginSubmitButton.text = 'Login';
      } else {
        loginSubmitButton.text = 'Send magic link (or enter password)';
      }
    };
    loginForm.changeSubject.subscribe(() => {
      console.log(`checking button text ${loginPasswordInput.value}`);
      setButtonText();
    });
    setButtonText();

    const urlSearchParams = new URLSearchParams(window.location.search);
    const queryParams = Object.fromEntries((urlSearchParams as any).entries());
    if (queryParams.transfertoken) {
      loginForm.setStatus('pending', 'got transfer code. hold on...');
      const exchangeRefreshTokenAndTransferToken =
        new domtools.TypedRequest<lointReception.request.IRequestExchangeRefreshTokenAndTransferToken>(
          LeleLogin.receptionUrl,
          'exchangeRefreshTokenAndTransferToken'
        );
      const response = await exchangeRefreshTokenAndTransferToken
        .fire({
          transferToken: queryParams.transfertoken,
        })
        .catch(async (err) => {
          loginForm.setStatus('error', err.errorText);
          await this.domtools.convenience.smartdelay.delayFor(5000);
          loginForm.setStatus('error', 'Please login again!');
          await this.domtools.convenience.smartdelay.delayFor(1000);
          this.domtools.router.queryParams.deleteQueryParam('transfertoken');
          window.location.reload();
        });
      if (response) {
        const jwt = await this.handleRefreshToken(response.refreshToken, 0);
        loginForm.setStatus('success', `Got Jwt. You are logged in!`);
      }
    }
  }

  private login = async (valueArg: { emailAddress: string; passwordArg: string }) => {
    // lets define the needed requests
    const loginForm: DeesForm = this.shadowRoot.querySelector('#loginForm');
    const loginRequestWithUsernameAndPassword =
      new domtools.TypedRequest<lointReception.request.IRequestLoginWithEmailOrUsernameAndPassword>(
        LeleLogin.receptionUrl,
        'loginWithEmailOrUsernameAndPassword'
      );
    const loginRequestWithEmail =
      new domtools.TypedRequest<lointReception.request.IRequestLoginWithEmail>(
        LeleLogin.receptionUrl,
        'loginWithEmail'
      );

    // lets do the actual logging in
    if (valueArg.emailAddress && valueArg.passwordArg) {
      loginForm.setStatus('pending', 'logging in...');
      const response = await loginRequestWithUsernameAndPassword
        .fire({
          username: valueArg.emailAddress, // TODO: rename to emailAddress
          password: valueArg.passwordArg,
        })
        .catch(() => {
          loginForm.setStatus('error', 'could not log you in. Try Again!');
          return;
        });
      if (!response) {
        return;
      }
      if (response.refreshToken) {
        loginForm.setStatus('pending', 'obtained refreshToken...');
        const jwt = await this.handleRefreshToken(response.refreshToken, 0);
        if (jwt) {
          loginForm.setStatus('success', 'obtained jwt.');
        } else {
          loginForm.setStatus('error', 'something went wrong');
        }
      } else {
      }
    } else if (valueArg.emailAddress && !valueArg.passwordArg) {
      loginForm.setStatus('pending', 'sending magic link...');
      const response = await loginRequestWithEmail.fire({
        email: valueArg.emailAddress,
      });
      if (response.status === 'ok') {
        loginForm.setStatus('success', 'Please check your email!');
      }
      console.log(response);
    }
  };

  private register = async (valueArg: { emailAddress: string }) => {
    const registrationForm: DeesForm = this.shadowRoot.querySelector('#registrationForm');
    registrationForm.setStatus('pending', 'registering...');
    const firstSignupRequest =
      new domtools.TypedRequest<lointReception.request.IRequestFirstRegistration>(
        LeleLogin.receptionUrl,
        'firstRegistrationRequest'
      );
    const response = await firstSignupRequest
      .fire({
        email: valueArg.emailAddress,
        productSlugOfInterest: this.productOfInterest,
      })
      .catch((err) => {
        registrationForm.setStatus('error', err.message);
        return null;
      });
    if (response.status === 'ok') {
      registrationForm.setStatus('success', 'Please check your email!');
    }
    console.log(response);
  };

  public async dispatchJwt(jwtArg?: string) {
    if (jwtArg !== undefined) {
      console.log(jwtArg);
      this.jwt = jwtArg;
      await domtools.plugins.smartdelay.delayFor(200);
      this.dispatchEvent(
        new CustomEvent('leleLoginGotJwt', {
          detail: {
            jwt: this.jwt,
          },
        })
      );
      this.jwtObserable.next(this.jwt);
    }
  }

  public async handleRefreshToken(refreshTokenArg: string, delayDispatchMillisArg = 0) {
    // a refreshToken binds dierctly to a session.
    // the refresh token is used on a continuous basis to get fresh and short-lived jwts
    const refreshJwt = new domtools.TypedRequest<lointReception.request.IRequestRefreshJwt>(
      LeleLogin.receptionUrl,
      'refreshJwt'
    );
    const responseJwt = await refreshJwt.fire({
      refreshToken: refreshTokenArg,
    });

    if (responseJwt.jwt) {
      this.domtools.convenience.smartdelay.delayFor(delayDispatchMillisArg).then(() => {
        this.dispatchJwt(responseJwt.jwt);
      });
      return responseJwt.jwt;
    } else {
      return null;
    }
  }
}
